import { useState } from 'react'
import { ErrorBanner } from '@/components/AppLayout'
import { Link } from 'react-router-dom'
import {
  previewImport,
  runImport,
  type CredentialSource,
  type ImportPreview,
} from '@/lib/eventbriteImport'
import type { ImportSummary } from '@/types/database'

interface Props {
  eventId: string
  defaultEventbriteEventId: string | null
  onImported: () => void
  onClose: () => void
}

const FIELD_LABELS: Record<string, string> = {
  first_name: 'first name',
  last_name: 'last name',
  email: 'email',
  phone: 'phone',
  gender: 'gender',
}

/**
 * Eventbrite import, always previewed before it writes.
 *
 * The preview is the same diff the database will apply — it comes from the same
 * planner, run server-side by the Edge Function — so "3 new, 1 changed, 2 edits
 * preserved" is what will actually happen rather than an estimate.
 */
export function ImportPanel({ eventId, defaultEventbriteEventId, onImported, onClose }: Props) {
  const [eventbriteEventId, setEventbriteEventId] = useState(defaultEventbriteEventId ?? '')
  const [cancelMissing, setCancelMissing] = useState(true)
  const [genderQuestion, setGenderQuestion] = useState('')
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [result, setResult] = useState<ImportSummary | null>(null)
  const [source, setSource] = useState<CredentialSource | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const request = {
    eventId,
    eventbriteEventId: eventbriteEventId.trim(),
    cancelMissing,
    ...(genderQuestion.trim() === '' ? {} : { genderQuestion: genderQuestion.trim() }),
  }

  async function handlePreview() {
    setBusy(true)
    setError(null)
    setResult(null)
    try {
      const next = await previewImport(request)
      setPreview(next)
      setSource(next.credential_source)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reach Eventbrite')
    } finally {
      setBusy(false)
    }
  }

  async function handleApply() {
    setBusy(true)
    setError(null)
    try {
      const next = await runImport(request)
      setResult(next.result)
      setSource(next.credential_source)
      setPreview(null)
      onImported()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The import failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2>Import from Eventbrite</h2>
        <button className="btn btn-sm btn-ghost" type="button" onClick={onClose}>
          Close
        </button>
      </div>

      <div className="field">
        <label className="field-label" htmlFor="eb-event">
          Eventbrite event ID
        </label>
        <p className="field-hint">The number at the end of the Eventbrite event URL.</p>
        <input
          id="eb-event"
          type="text"
          value={eventbriteEventId}
          onChange={(e) => {
            setEventbriteEventId(e.target.value)
          }}
          placeholder="1234567890"
        />
      </div>

      <div className="field">
        <label className="field-label" htmlFor="eb-gender">
          Gender question label
        </label>
        <p className="field-hint">
          Optional. If your ticket form asks for gender as a custom question, put its exact wording
          here.
        </p>
        <input
          id="eb-gender"
          type="text"
          value={genderQuestion}
          onChange={(e) => {
            setGenderQuestion(e.target.value)
          }}
          placeholder="Gender"
        />
      </div>

      <label className="checkbox">
        <input
          type="checkbox"
          checked={cancelMissing}
          onChange={(e) => {
            setCancelMissing(e.target.checked)
          }}
        />
        Mark attendees who are no longer on Eventbrite as cancelled
      </label>

      {error !== null && <ErrorBanner message={error} />}

      {source === 'stub' && (
        <p className="banner banner-warning">
          No Eventbrite account is connected, so this used sample data. Connect one in{' '}
          <Link to="/settings">Settings</Link> to import for real.
        </p>
      )}

      {source === 'deployment' && (
        <p className="banner banner-warning">
          This read a shared Eventbrite account configured for the deployment, not this
          organization&rsquo;s own. Connect an account in <Link to="/settings">Settings</Link> to
          use your own.
        </p>
      )}

      {preview !== null && (
        <div className="stack">
          <h3>What this import would do</h3>
          <div className="metric-grid">
            <div className="metric">
              <span className="metric-label">New</span>
              <span className="metric-value">{preview.plan.summary.created}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Changed</span>
              <span className="metric-value">{preview.plan.summary.updated}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Unchanged</span>
              <span className="metric-value">{preview.plan.summary.unchanged}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Cancelled</span>
              <span className="metric-value">{preview.plan.summary.cancelled}</span>
            </div>
          </div>

          {preview.plan.summary.fieldsPreserved > 0 && (
            <p className="banner banner-success">
              {preview.plan.summary.fieldsPreserved} hand-edited{' '}
              {preview.plan.summary.fieldsPreserved === 1 ? 'field' : 'fields'} will be kept as you
              edited {preview.plan.summary.fieldsPreserved === 1 ? 'it' : 'them'}.
            </p>
          )}

          {preview.plan.updates.length > 0 && (
            <div className="table-responsive">
              <div className="table-wrap">
                <table>
                  <caption className="sr-only">Changes this import would make</caption>
                  <thead>
                    <tr>
                      <th scope="col">Number</th>
                      <th scope="col">Changes</th>
                      <th scope="col">Kept</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.plan.updates.map((update) => (
                      <tr key={update.attendeeId}>
                        <td data-label="Number" className="cell-number">
                          {update.matchmakingId}
                        </td>
                        <td data-label="Changes">
                          {update.changes.length === 0
                            ? '—'
                            : update.changes
                                .map(
                                  (c) =>
                                    `${FIELD_LABELS[c.field] ?? c.field}: ${c.from ?? '—'} → ${c.to ?? '—'}`
                                )
                                .join('; ')}
                        </td>
                        <td data-label="Kept" className="cell-muted">
                          {update.preserved.length === 0
                            ? '—'
                            : update.preserved
                                .map(
                                  (c) =>
                                    `${FIELD_LABELS[c.field] ?? c.field} stays ${c.from ?? '—'}`
                                )
                                .join('; ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {preview.skipped_rows > 0 && (
            <p className="field-hint">
              {preview.skipped_rows} Eventbrite {preview.skipped_rows === 1 ? 'record' : 'records'}{' '}
              had no usable name and will be skipped.
            </p>
          )}
        </div>
      )}

      {result !== null && (
        <>
          <p className="banner banner-success">
            Imported: {result.created} new, {result.updated} changed, {result.unchanged} unchanged,{' '}
            {result.cancelled} cancelled, {result.fields_preserved} edits preserved.
          </p>
          {result.skipped > 0 && (
            <p className="field-hint">
              {result.skipped} {result.skipped === 1 ? 'order was' : 'orders were'} cancelled,
              refunded or voided on Eventbrite and {result.skipped === 1 ? 'was' : 'were'} not
              imported.
            </p>
          )}
        </>
      )}

      <div className="row btn-group-stack">
        <button
          className="btn"
          type="button"
          onClick={() => void handlePreview()}
          disabled={busy || request.eventbriteEventId === ''}
        >
          {busy ? 'Working…' : 'Preview'}
        </button>
        <button
          className="btn btn-primary"
          type="button"
          onClick={() => void handleApply()}
          disabled={busy || preview === null}
          // Preview first: an import that cancels people should never be a
          // single click away.
          title={preview === null ? 'Preview the import first' : undefined}
        >
          Apply import
        </button>
      </div>
    </div>
  )
}
